// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Login Page
// Single rounded card: photo left, form right (FOIA-style)
// Supports both username/password and OpenGov SSO.
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextInput, PasswordInput, Button, Alert } from '../../components/gab/GabComponents';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { startOidcLogin } from '../../auth/oidc';
import './Login.css';

export default function Login() {
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const { login, isLoading, error } = useAuthStore();
  const navigate                  = useNavigate();
  const [searchParams]            = useSearchParams();
  const ssoError                  = searchParams.get('error');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      navigate('/');
    } catch {
      // Error is set in store
    }
  };

  const handleSso = () => {
    setSsoLoading(true);
    startOidcLogin().catch(() => setSsoLoading(false));
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#DDDEDE',
      padding: '24px',
    }}>
      {/* Single rounded card */}
      <div style={{
        display: 'flex',
        width: '100%',
        maxWidth: 1100,
        minHeight: 680,
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid #e2e4e8',
        background: '#fff',
      }}>

        {/* Left — photo panel (hidden on mobile) */}
        <div className="login-photo-panel">
          <img
            src="/disaster.jpg"
            alt="Emergency scene"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '65% center', display: 'block' }}
          />
          {/* Dark gradient overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)',
          }} />
          {/* Caption at bottom */}
          <div style={{
            position: 'absolute',
            bottom: 32,
            left: 32,
            right: 32,
            color: '#fff',
          }}>
            <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2, marginBottom: 8 }}>
              Emergency Management Console
            </div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', fontWeight: 400 }}>
              OpenGov — Unified incident command &amp; recovery operations
            </div>
          </div>
        </div>

        {/* Right — form panel */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 40px',
          background: '#fff',
        }}>
          <div style={{ width: '100%', maxWidth: 360 }}>
            {/* Logo */}
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <img src="/opengov_logo.png" alt="OpenGov" style={{ height: 40 }} />
            </div>

            {/* Heading */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#1B1C1C', marginBottom: 4 }}>Sign in</div>
              <div style={{ fontSize: 14, color: '#7B7D7F' }}>Enter your credentials to access the console</div>
            </div>

            {/* Form card */}
            <div style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              padding: '28px 24px',
            }}>
              {(error || ssoError) && (
                <Alert icon={<IconAlertCircle size={16} />} color="var(--color-error)" mb="md" variant="light">
                  {error || ssoError}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextInput
                  label="Email"
                  placeholder="Username or email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  size="md"
                  mb="md"
                />
                <PasswordInput
                  label="Password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  size="md"
                  mb="lg"
                  autoComplete="current-password"
                />
                <Button
                  type="submit"
                  fullWidth
                  loading={isLoading}
                  size="md"
                  color="var(--color-primary-700)"
                >
                  Continue
                </Button>
              </form>

              {/* Divider */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                margin: '20px 0',
              }}>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>or</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              </div>

              {/* SSO button */}
              <Button
                fullWidth
                size="md"
                color="transparent"
                loading={ssoLoading}
                onClick={handleSso}
                style={{
                  border: '1px solid #cbd5e1',
                  color: '#1e293b',
                  background: '#fff',
                  fontWeight: 500,
                }}
              >
                Continue with OpenGov SSO
              </Button>
            </div>

            {/* Footer */}
            <div style={{ marginTop: 20, textAlign: 'center', color: '#939598', fontSize: 14 }}>
              © {new Date().getFullYear()} OpenGov, Inc. All rights reserved.
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
